import { Mail, Paperclip, ExternalLink } from "lucide-react";
import { useRouter } from "next/navigation";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { formatRelativeTime } from "@/app/_module/utils/formatters";
import type { EmailHistory, EmailStatus } from "../types/email-history.types";

interface EmailListProps {
  emails: EmailHistory[];
  onEmailClick?: (email: EmailHistory) => void;
}

export const EmailList = function({ emails, onEmailClick }: EmailListProps) {
  const router = useRouter();

  // Helper to get email status (backend returns emailStatus, type expects status as alias)
  const getEmailStatus = (email: EmailHistory): EmailStatus => {
    return (email.emailStatus || email.status || 'sent') as EmailStatus;
  };

  const handleEmailClick = (email: EmailHistory) => {
    if (onEmailClick) {
      onEmailClick(email);
    } else if (email.leadId) {
      // Navigate to lead details page with history tab open
      router.push(`/leads/${email.leadId}?tab=history`);
    }
  };

  const getStatusBadge = (status: EmailStatus) => {
    const colors: Record<string, string> = {
      queued: "bg-gray-100 text-gray-800",
      sending: "bg-blue-100 text-blue-800",
      sent: "bg-blue-100 text-blue-800",
      delivered: "bg-green-100 text-green-800",
      received: "bg-green-100 text-green-800", // For inbound emails
      opened: "bg-purple-100 text-purple-800",
      clicked: "bg-indigo-100 text-indigo-800",
      failed: "bg-red-100 text-red-800",
      bounced: "bg-orange-100 text-orange-800",
      unsubscribed: "bg-gray-100 text-gray-800",
    };

    return (
      <Badge className={`text-xs ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getProviderBadge = (sendMethod: string) => {
    const labels: Record<string, string> = {
      gmail_oauth: "Gmail",
      outlook_oauth: "Outlook",
      sendgrid: "SendGrid",
    };

    const colors: Record<string, string> = {
      gmail_oauth: "bg-red-50 text-red-700 border-red-200",
      outlook_oauth: "bg-blue-50 text-blue-700 border-blue-200",
      sendgrid: "bg-purple-50 text-purple-700 border-purple-200",
    };

    return (
      <Badge variant="outline" className={`text-xs ${colors[sendMethod] || "bg-gray-50 text-gray-700"}`}>
        {labels[sendMethod] || sendMethod}
      </Badge>
    );
  };

  // Clean email body preview - remove quoted replies and extra whitespace
  const cleanEmailPreview = (bodyText: string | null | undefined, bodyHtml: string | null | undefined): string => {
    let text = bodyText || '';

    // If no plain text, extract from HTML
    if (!text && bodyHtml) {
      text = bodyHtml.replace(/<[^>]*>/g, ' ');
    }

    if (!text) return 'No content';

    // Remove Gmail-style quoted text: "On Mon, Jan 11... wrote:"
    text = text.replace(/On\s+\w+,\s+\w+\s+\d+,\s+\d+\s+at\s+[\d:]+\s*[AP]M.*?wrote:/gi, '');

    // Remove Outlook-style quoted text: "From: ... Sent: ... To: ... Subject:"
    text = text.replace(/From:[\s\S]*?Subject:[\s\S]*?(?=\n|$)/gi, '');

    // Remove lines starting with > (quoted text)
    text = text.replace(/^>.*$/gm, '');

    // Remove "---------- Forwarded message ----------" blocks
    text = text.replace(/-+\s*Forwarded message\s*-+[\s\S]*$/gi, '');

    // Remove "---------- Original Message ----------" blocks
    text = text.replace(/-+\s*Original Message\s*-+[\s\S]*$/gi, '');

    // Clean up multiple newlines and whitespace
    text = text.replace(/\n{3,}/g, '\n\n');
    text = text.replace(/\s+/g, ' ');

    return text.trim() || 'No content';
  };

  if (emails.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No emails found.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {emails.map((email) => (
        <div
          key={email.id}
          className="flex items-start justify-between p-4 bg-card rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
          onClick={() => handleEmailClick(email)}
        >
          <div className="flex items-start space-x-3 flex-1 min-w-0">
            <Mail
              className={`h-4 w-4 mt-1 ${
                email.emailType === "inbound" ? "text-green-600" : "text-blue-600"
              }`}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                {getStatusBadge(getEmailStatus(email))}
                {email.lead?.firstName && email.lead?.lastName ? (
                  <p className="text-sm font-medium text-blue-600">
                    Lead: {email.lead.firstName} {email.lead.lastName}
                  </p>
                ) : (
                  <p className="text-sm font-medium text-foreground truncate">
                    {email.emailType === "inbound" ? email.fromEmail : email.toEmail}
                  </p>
                )}
                {getProviderBadge(email.sendMethod)}
                {email.attachmentCount > 0 && (
                  <Badge variant="outline" className="text-xs bg-gray-50">
                    <Paperclip className="h-3 w-3 mr-1" />
                    {email.attachmentCount}
                  </Badge>
                )}
              </div>
              <div className="flex items-center space-x-2 mb-2 flex-wrap">
                <p className="text-xs text-muted-foreground">
                  {email.emailType === "inbound"
                    ? `From: ${email.fromEmail}`
                    : `To: ${email.toEmail}`}
                </p>
                <span className="text-xs text-muted-foreground">•</span>
                <p className="text-xs text-muted-foreground">
                  {email.emailType === "inbound" ? "Received" : "Sent"}
                </p>
              </div>
              <p className="text-sm font-medium text-foreground mb-1 truncate">
                {email.subject}
              </p>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {cleanEmailPreview(email.bodyText, email.bodyHtml)}
              </p>

              {/* Email engagement stats */}
              {email.emailType === "outbound" && (email.openedAt || email.clickedAt) && (
                <div className="flex items-center space-x-3 mt-2 text-xs text-muted-foreground">
                  {email.openedAt && (
                    <span className="flex items-center">
                      <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mr-1"></span>
                      Opened {formatRelativeTime(new Date(email.openedAt))}
                    </span>
                  )}
                  {email.clickedAt && (
                    <span className="flex items-center">
                      <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mr-1"></span>
                      Clicked {formatRelativeTime(new Date(email.clickedAt))}
                    </span>
                  )}
                </div>
              )}

              {/* Error message for failed emails */}
              {getEmailStatus(email) === "failed" && email.errorMessage && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <span className="font-medium">Error:</span> {email.errorMessage}
                </div>
              )}
            </div>
          </div>
          <div className="text-right ml-4 flex flex-col items-end gap-2">
            <p className="text-xs text-muted-foreground">
              {formatRelativeTime(new Date(email.sentAt))}
            </p>
            {(email.leadId || email.lead) && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  router.push(`/leads/${email.leadId || email.lead?.id}?tab=history`);
                }}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                View Lead
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
